import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { menuItemService } from '../../../../services/menuItemService';
import { MenuItemDTO } from '../../../../types';
import { ArrowLeft, SquarePen, Trash2, ChefHat, FlaskConical } from 'lucide-react';
import '../RestaurantPage.css';

const MenuItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<MenuItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await menuItemService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setItem(d);
    } catch {
      setError(t('restaurant.menuItem.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!window.confirm(t('restaurant.menuItem.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await menuItemService.delete(item.id);
      navigate('/restaurant/menu-items');
    } catch {
      setError(t('restaurant.menuItem.failedToDelete'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-page rest-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="ims-page rest-page">
          <div className="ims-alert ims-alert-error">{error ?? t('restaurant.menuItem.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-detail-page rest-detail-page">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="ims-hero rest-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/restaurant/menu-items')}>
              <ArrowLeft size={14} />
              {t('restaurant.menuItem.title')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{item.name}</h1>
              {item.categoryName && (
                <p className="ims-hero-supplier">
                  {item.categoryName}
                  {item.typeName && ` · ${item.typeName}`}
                </p>
              )}
              <div className="ims-hero-badges">
                <span className={`ims-hero-badge ${item.isAvailable ? 'rest-badge-available' : 'rest-badge-unavailable'}`}>
                  {item.isAvailable ? t('restaurant.menuItem.available') : t('restaurant.menuItem.unavailable')}
                </span>
                <span className={`ims-hero-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                  {item.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="ims-btn ims-btn-secondary"
                  onClick={() => navigate(`/restaurant/menu-items/${item.id}/edit`)}
                >
                  <SquarePen size={14} style={{ marginInlineEnd: 4 }} />
                  {t('common.edit')}
                </button>
                <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                  <Trash2 size={14} style={{ marginInlineEnd: 4 }} />
                  {deleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
              <span className="ims-hero-date">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBlock: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="ims-detail-body">
          {/* Basic Info */}
          <div className="ims-detail-main">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <ChefHat size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('restaurant.menuItem.basicInfo')}
                </span>
              </div>
              <div className="ims-card-body">
                <div className="rest-fields-grid">
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuItem.name')}</div>
                    <div className="rest-field-value strong">{item.name}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuItem.price')}</div>
                    <div className="rest-field-value price">{Number(item.price).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuItem.category')}</div>
                    <div className="rest-field-value">{item.categoryName || '—'}</div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuCategory.type')}</div>
                    <div className="rest-field-value">
                      {item.typeName ? (
                        <span className="ims-badge rest-badge-type">{item.typeName}</span>
                      ) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuItem.available')}</div>
                    <div className="rest-field-value">
                      <span className={`ims-badge ${item.isAvailable ? 'rest-badge-available' : 'rest-badge-unavailable'}`}>
                        {item.isAvailable ? t('restaurant.menuItem.available') : t('restaurant.menuItem.unavailable')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('common.status')}</div>
                    <div className="rest-field-value">
                      <span className={`ims-badge ${item.isActive ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
                        {item.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuItem.prepTime')}</div>
                    <div className="rest-field-value">
                      {item.prepTimeMinutes != null ? `${item.prepTimeMinutes} min` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="rest-field-label">{t('restaurant.menuType.sortOrder')}</div>
                    <div className="rest-field-value">{item.sortOrder}</div>
                  </div>
                  <div className="rest-field-full">
                    <div className="rest-field-label">{t('common.description')}</div>
                    <div className="rest-field-value">{item.description || '—'}</div>
                  </div>
                  {item.imageUrl && (
                    <div className="rest-field-full">
                      <div className="rest-field-label">{t('restaurant.menuItem.image', 'Image')}</div>
                      <div className="rest-field-value">
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border-color)' }}
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="rest-field-label">{t('common.createdAt')}</div>
                    <div className="rest-field-value">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recipe sidebar */}
          <div className="ims-detail-sidebar">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <FlaskConical size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('restaurant.recipe.title')}
                </span>
              </div>
              <div className="ims-card-body" style={{ overflowX: 'auto', padding: 0 }}>
                {!item.recipes || item.recipes.length === 0 ? (
                  <div style={{ color: 'var(--ims-text-sub)', padding: '1.25rem', textAlign: 'center' }}>
                    {t('restaurant.recipe.noIngredients')}
                  </div>
                ) : (
                  <table className="rest-recipe-table">
                    <thead>
                      <tr>
                        <th>{t('restaurant.recipe.product')}</th>
                        <th>{t('restaurant.recipe.unit')}</th>
                        <th>Unit Cost</th>
                        <th>Line Cost</th>
                        <th>{t('restaurant.recipe.quantity')}</th>
                        <th>{t('common.notes')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.recipes.map((r) => (
                        <tr key={r.id}>
                          <td>
                            {r.productName || '—'}
                            {r.productCode && (
                              <span style={{ color: 'var(--ims-text-sub)', fontSize: '0.72rem' }}>
                                {' '}({r.productCode})
                              </span>
                            )}
                          </td>
                          <td>{r.unitName || '—'}</td>
                          <td style={{ color: 'var(--ims-text-sub)', fontSize: '0.8rem' }}>
                            {r.unitCost != null ? `$${Number(r.unitCost).toFixed(4)}` : '—'}
                          </td>
                          <td style={{ fontWeight: 500 }}>
                            {r.lineCost != null ? `$${Number(r.lineCost).toFixed(4)}` : '—'}
                          </td>
                          <td style={{ fontWeight: 600 }}>{r.quantity}</td>
                          <td style={{ color: 'var(--ims-text-sub)' }}>{r.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Costing */}
            {(item.foodCost !== undefined && item.price > 0) && (
              <div className="ims-card" style={{ marginTop: '1rem' }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">Costing Analysis</span>
                </div>
                <div className="ims-card-body">
                  <div className="rest-fields-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div>
                      <div className="rest-field-label">Food Cost</div>
                      <div className="rest-field-value price">${Number(item.foodCost ?? 0).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="rest-field-label">Selling Price</div>
                      <div className="rest-field-value price">${Number(item.price).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="rest-field-label">Gross Profit</div>
                      <div className="rest-field-value" style={{ color: (item.grossProfit ?? 0) >= 0 ? 'var(--ims-green-text, #16a34a)' : 'var(--ims-red-text, #dc2626)', fontWeight: 600 }}>
                        ${Number(item.grossProfit ?? 0).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="rest-field-label">Food Cost %</div>
                      <div className="rest-field-value" style={{ fontWeight: 600, color: (item.foodCostPercent ?? 0) > 40 ? 'var(--ims-red-text, #dc2626)' : (item.foodCostPercent ?? 0) > 30 ? '#d97706' : 'var(--ims-green-text, #16a34a)' }}>
                        {Number(item.foodCostPercent ?? 0).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Operational details */}
            {(item.kotBotType || item.serviceType || item.taxRate) && (
              <div className="ims-card" style={{ marginTop: '1rem' }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">Operational Details</span>
                </div>
                <div className="ims-card-body">
                  <div className="rest-fields-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    {item.menuCode && <div><div className="rest-field-label">Menu Code</div><div className="rest-field-value"><code>{item.menuCode}</code></div></div>}
                    {item.kotBotType && item.kotBotType !== 'None' && <div><div className="rest-field-label">KOT/BOT</div><div className="rest-field-value">{item.kotBotType}</div></div>}
                    {item.serviceType && item.serviceType !== 'All' && <div><div className="rest-field-label">Service Type</div><div className="rest-field-value">{item.serviceType}</div></div>}
                    {(item.taxRate != null && item.taxRate > 0) && <div><div className="rest-field-label">Tax Rate</div><div className="rest-field-value">{item.taxRate}%</div></div>}
                    {(item as any).taxType && <div><div className="rest-field-label">{t('restaurant.taxType')}</div><div className="rest-field-value">{(item as any).taxType}</div></div>}
                    {item.availableFrom && <div><div className="rest-field-label">Available From</div><div className="rest-field-value">{item.availableFrom}</div></div>}
                    {item.availableTo && <div><div className="rest-field-label">Available To</div><div className="rest-field-value">{item.availableTo}</div></div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MenuItemDetailPage;
